import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as km from '../api/keyManagementApi';
import { getAllTenants, Tenant } from '../api/tenantApi';
import { showModal } from '../store/slices/uiSlice';

// ── Types ─────────────────────────────────────────────────────────────────────

type OpState = 'idle' | 'loading' | 'ok' | 'error';

/**
 * The state of the last STATUS READ for a row, deliberately not OpState.
 *
 * 'unknown' is the state that was missing and the reason a working "Initialize All" could paint
 * itself red and leave every row reading "Missing": a status read that failed says nothing about
 * whether the key exists. It is not 'error' (the key may be perfectly fine — we could not look)
 * and it is emphatically not "no currentKey", which is what the badge used to fall through to.
 */
type LoadState = 'idle' | 'loading' | 'ok' | 'unknown';

interface OpResult {
  state: OpState;
  summary: string;
  detail?: string;
}

interface ServiceRow {
  service: string;
  status: km.KeyStatus | null;
  loadState: LoadState;
  /** Why the status read could not be completed. Only set when loadState === 'unknown'. */
  loadError?: string;
  op: OpResult;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ageColor = (days?: number) => {
  if (days === undefined) return 'text-gray-400';
  if (days > 25) return 'text-red-400 font-semibold';
  if (days > 20) return 'text-yellow-400 font-semibold';
  return 'text-green-400';
};

const fmtDate = (s?: string | Date) => {
  if (!s) return '—';
  const d = typeof s === 'string' ? new Date(s) : s;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDatetime = (s?: string) =>
  s ? new Date(s).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

// Extract human-readable error from any shape the server might return
function extractError(e: any): { summary: string; detail: string } {
  // axios error with a response body
  const body = e?.response?.data;
  if (body) {
    // IDP / vault error body: { error: { code, message } }
    if (body.error?.message) return { summary: body.error.code ?? 'Error', detail: body.error.message };
    // BFF error body: { error: '...' }
    if (typeof body.error === 'string') return { summary: 'Error', detail: body.error };
    // IDP wrapped error: { message: '...' }
    if (body.message) return { summary: 'Error', detail: body.message };
  }
  // plain Error
  return { summary: 'Error', detail: e?.message ?? 'Unknown error' };
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ row }: { row: ServiceRow }) {
  const { status, loadState, op, loadError } = row;

  // PRECEDENCE: the row's own operation outcome outranks the status read, always.
  //
  // runOp refreshes the row immediately after a successful initialize/rotate/cleanup, and that
  // refresh can fail on its own — IdentityProvider performs the vault WRITE with its own
  // identity, while AppManagement READS with the Container App MSI, so the write can succeed
  // against a vault the read is forbidden on. Testing the read state first repainted a 200 OK
  // initialize as "vault unreachable": the operation succeeded and the panel said it had not.
  if (op.state === 'loading') return <span className="text-blue-400 text-xs animate-pulse">working…</span>;

  if (op.state === 'error') return (
    <span className="inline-flex items-center gap-1 text-xs text-red-400 cursor-help" title={op.detail}>
      <span className="text-red-500">✗</span>
      <span className="max-w-[180px] truncate">{op.summary}</span>
      {op.detail && <span className="text-red-500/70">— {op.detail.slice(0, 80)}{op.detail.length > 80 ? '…' : ''}</span>}
    </span>
  );

  if (op.state === 'ok') return (
    <span className="inline-flex items-center gap-1 text-xs text-green-400">
      <span>✓</span> {op.summary}
    </span>
  );

  // No operation to report — now, and only now, the status read has the floor.
  if (loadState === 'loading') return <span className="text-gray-500 text-xs animate-pulse">reading…</span>;

  // "could not check" and "not there" are different answers and must not share a badge.
  if (loadState === 'unknown') return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-400 cursor-help"
      title={loadError ?? 'The key status could not be read; this says nothing about whether the key exists.'}>
      <span>?</span>
      <span className="text-xs bg-amber-900/40 text-amber-400 px-1.5 py-0.5 rounded-full">Unknown</span>
    </span>
  );

  // Idle — show vault state
  if (!status) return <span className="text-gray-600 text-xs">—</span>;
  if (!status.currentKey) return (
    <span className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full">Missing</span>
  );
  if (status.needsRotation) return (
    <span className="text-xs bg-orange-900/50 text-orange-400 px-1.5 py-0.5 rounded-full">Rotate needed</span>
  );
  return (
    <span className="text-xs bg-green-900/40 text-green-400 px-1.5 py-0.5 rounded-full">OK</span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export const KeyManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState(params.get('tenantId') ?? '');
  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [initAllOp, setInitAllOp] = useState<OpResult>({ state: 'idle', summary: '' });
  const [initAllDetail, setInitAllDetail] = useState<Record<string, { ok: boolean; msg: string }>>({});

  const [rotationStatus, setRotationStatus] = useState<km.KeyRotationStatusResponse | null>(null);
  const [rotationProxiedVia, setRotationProxiedVia] = useState<km.RotationStatusProxiedVia | null>(null);
  const [rotationLoading, setRotationLoading] = useState(false);
  const [rotationError, setRotationError] = useState<string | null>(null);
  const [rotationOpen, setRotationOpen] = useState(true);

  useEffect(() => {
    getAllTenants().then(r => setTenants((r as any).data ?? [])).catch(() => {});
  }, []);

  // Rotation Health follows the SELECTED tenant.
  //
  // With no tenant selected the panel does not call at all. The alternative - calling without a
  // tenantId - hands the choice to the controller, which then picks a tenant itself (first
  // active one with a resolvable BFF URL); that is precisely how a developmentTenant session
  // ended up silently reporting andreas.demo.ontoris.app's rotation health. An empty panel that
  // says "select a tenant" is honest; a populated panel about a tenant nobody chose is not.
  useEffect(() => {
    if (!selectedTenantId) {
      setRotationStatus(null);
      setRotationProxiedVia(null);
      setRotationError(null);
      setRotationLoading(false);
      return;
    }
    setRotationLoading(true);
    setRotationError(null);
    km.getKeyRotationStatus(selectedTenantId)
      .then((r: any) => {
        setRotationStatus(r.data ?? null);
        setRotationProxiedVia(r.proxiedVia ?? null);
      })
      .catch((e: any) => {
        setRotationStatus(null);
        setRotationProxiedVia(null);
        setRotationError(e?.message ?? 'Failed to load rotation status');
      })
      .finally(() => setRotationLoading(false));
  }, [selectedTenantId]);

  const loadAll = useCallback(async (tenantId: string) => {
    const t = tenants.find(x => x.tenantId === tenantId);
    if (!t?._id) return;
    setLoadingAll(true);
    setLoadError(null);
    try {
      const r: any = await km.getAllStatuses(t._id);
      if (!r.success) { setLoadError(r.message ?? 'Failed'); return; }
      const data: Record<string, km.KeyStatus> = r.data ?? {};
      // getAllStatuses answers a per-service failure with { serviceName, error } and NO
      // currentKey. Treating that as a normal row rendered it as "Missing" — so "Initialize All"
      // could report "All 13 initialized" over 13 rows all claiming the key was absent. A 404 is
      // already reported as currentKey: null by the server; an `error` field means the read
      // itself failed, which is 'unknown'.
      setRows(Object.entries(data).map(([svc, status]) => {
        const readError = (status as any)?.error as string | undefined;
        return {
          service: svc,
          status: readError ? null : status,
          loadState: (readError ? 'unknown' : 'ok') as LoadState,
          loadError: readError,
          op: { state: 'idle', summary: '' },
        };
      }));
    } catch (e: any) {
      setLoadError(e.response?.data?.message ?? e.message);
    } finally {
      setLoadingAll(false);
    }
  }, [tenants]);

  useEffect(() => {
    if (selectedTenantId && tenants.length > 0) loadAll(selectedTenantId);
  }, [selectedTenantId, tenants]);

  const updateRow = (service: string, patch: Partial<ServiceRow>) =>
    setRows(prev => prev.map(r => r.service === service ? { ...r, ...patch } : r));

  const refreshOneRow = useCallback(async (service: string, tenantId: string) => {
    const t = tenants.find(x => x.tenantId === tenantId);
    if (!t?._id) return;
    updateRow(service, { loadState: 'loading' });
    try {
      const r: any = await km.getStatus(t._id, service);
      updateRow(service, { status: r.data, loadState: 'ok', loadError: undefined });
    } catch (e: any) {
      // NOT 'error', and the row's op result is left untouched. This runs immediately after a
      // successful initialize/rotate/cleanup; a failure here means the status could not be read,
      // never that the operation failed or that the key is absent.
      const { detail } = extractError(e);
      updateRow(service, { status: null, loadState: 'unknown', loadError: detail });
    }
  }, [tenants]);

  // IdentityProvider answers a skipped rotation with HTTP 200 + success:true and
  // wasRotated:false, so it used to read as a success here and nothing happened.
  // Offer the same force-confirm the WebBFFUI Configuration tab offers.
  const promptForceRotate = (service: string, dueAt?: string) => {
    dispatch(showModal({
      title: 'Key not yet due for rotation',
      confirmLabel: 'Force Rotate',
      cancelLabel: 'Cancel',
      content: (
        <div className="space-y-3">
          <p className="text-gray-700 dark:text-gray-300 text-sm">
            The key for <strong className="font-mono">{service}</strong> is not yet due for rotation.
            {dueAt && <> It is scheduled for rotation on <strong>{fmtDatetime(dueAt)}</strong>.</>}
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Force rotation bypasses the due-date check. Use this only for emergency scenarios
            (suspected key compromise, compliance requirement). The current key moves to the
            previous slot and remains valid for the grace period, so active tokens are not
            immediately revoked.
          </p>
        </div>
      ),
      onConfirm: () => { void runOp(service, 'rotate', true); },
    }));
  };

  const runOp = async (service: string, op: 'initialize' | 'rotate' | 'cleanup', force = false) => {
    if (!selectedTenantId) return;
    updateRow(service, { op: { state: 'loading', summary: '' } });
    try {
      let r: any;
      if (op === 'initialize') r = await km.initializeKey(selectedTenantId, service);
      else if (op === 'rotate') r = await km.rotateKey(selectedTenantId, service, force);
      else r = await km.cleanupKeys(selectedTenantId, service);

      const d = r?.data ?? r;
      if (d?.success === false) {
        const { summary, detail } = extractError({ message: d.error, response: { data: d } });
        updateRow(service, { op: { state: 'error', summary, detail } });
        return;
      }

      // Rotate: `success: true` does NOT mean the key changed — only wasRotated does.
      if (op === 'rotate' && d?.wasRotated === false) {
        updateRow(service, { op: { state: 'idle', summary: '' } });
        if (d?.reason === 'not_yet_due') {
          promptForceRotate(service, d?.dueAt);
        } else {
          updateRow(service, {
            op: {
              state: 'error',
              summary: 'Not rotated',
              detail: `IdentityProvider skipped the rotation${d?.reason ? ` (reason: ${d.reason})` : ''}.`,
            },
          });
        }
        return;
      }

      let summary = '';
      if (op === 'cleanup') summary = `Cleaned ${d.cleanedKeysCount ?? 0} old key(s)`;
      else if (op === 'initialize') summary = `Initialized${d.algorithm ? ` · ${d.algorithm}` : ''}`;
      else summary = `Rotated${d.newKeyVersion ? ` → v${d.newKeyVersion}` : ''}${d.algorithm ? ` · ${d.algorithm}` : ''}`;
      updateRow(service, { op: { state: 'ok', summary } });
      await refreshOneRow(service, selectedTenantId);
    } catch (e: any) {
      const { summary, detail } = extractError(e);
      updateRow(service, { op: { state: 'error', summary, detail } });
    }
  };

  const handleInitAll = async () => {
    if (!selectedTenantId) return;
    setInitAllOp({ state: 'loading', summary: '' });
    setInitAllDetail({});
    try {
      const r: any = await km.initializeAll(selectedTenantId);
      const results: Record<string, any> = r.results ?? {};
      const detail: Record<string, { ok: boolean; msg: string }> = {};
      for (const [svc, res] of Object.entries(results)) {
        const ok = (res as any).success !== false;
        const msg = ok
          ? `OK${(res as any).algorithm ? ` · ${(res as any).algorithm}` : ''}`
          : ((res as any).error ?? (res as any).message ?? 'Failed');
        detail[svc] = { ok, msg };
      }
      setInitAllDetail(detail);
      const ok = Object.values(detail).filter(x => x.ok).length;
      const total = Object.keys(detail).length;
      const failed = total - ok;
      setInitAllOp({
        state: failed > 0 ? 'error' : 'ok',
        summary: failed > 0 ? `${ok}/${total} initialized — ${failed} failed` : `All ${ok} initialized`,
        detail: failed > 0 ? Object.entries(detail).filter(([,v]) => !v.ok).map(([k,v]) => `${k}: ${v.msg}`).join(' | ') : undefined,
      });
      loadAll(selectedTenantId);
    } catch (e: any) {
      const { summary, detail } = extractError(e);
      setInitAllOp({ state: 'error', summary, detail });
    }
  };

  const selectedTenant = tenants.find(x => x.tenantId === selectedTenantId);
  const secretStore = (selectedTenant as any)?.secretStoreName;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-200">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Key Management</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            Status reads directly from Key Vault &nbsp;·&nbsp; Initialize / Rotate goes through BFF → IDP
          </p>
        </div>
      </div>

      {/* Tenant selector */}
      <div className="bg-gray-800 rounded-lg p-4 mb-5">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-300 shrink-0">Tenant</label>
          <select
            value={selectedTenantId}
            onChange={e => setSelectedTenantId(e.target.value)}
            className="flex-1 max-w-sm px-3 py-1.5 bg-gray-700 border border-gray-600 text-gray-100 rounded text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="">— Select a tenant —</option>
            {tenants.map(t => (
              <option key={t._id} value={t.tenantId}>{t.name ?? t.tenantId} ({t.tenantId})</option>
            ))}
          </select>

          {selectedTenantId && (
            secretStore
              ? <span className="text-xs text-gray-400 font-mono">{secretStore}.vault.azure.net</span>
              : <span className="text-xs text-red-400">⚠ secretStoreName not set on tenant</span>
          )}

          {selectedTenantId && (
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={() => loadAll(selectedTenantId)} disabled={loadingAll}
                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 disabled:opacity-40 text-white rounded text-sm font-medium flex items-center gap-1.5"
                title="Re-read from vault">
                {loadingAll
                  ? <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                  : '↻'} Refresh
              </button>
              <button onClick={handleInitAll} disabled={initAllOp.state === 'loading'}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded text-sm font-medium flex items-center gap-1.5">
                {initAllOp.state === 'loading' && (
                  <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                )}
                Initialize All
              </button>
            </div>
          )}
        </div>

        {/* Init-all result banner */}
        {initAllOp.state !== 'idle' && initAllOp.state !== 'loading' && (
          <div className={`mt-3 rounded p-3 text-sm ${initAllOp.state === 'ok' ? 'bg-green-900/30 border border-green-700 text-green-300' : 'bg-red-900/30 border border-red-700 text-red-300'}`}>
            <p className="font-medium">{initAllOp.summary}</p>
            {/* Per-service breakdown */}
            {Object.keys(initAllDetail).length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                {Object.entries(initAllDetail).map(([svc, { ok, msg }]) => (
                  <div key={svc} className="flex items-center gap-1.5">
                    <span className={ok ? 'text-green-400' : 'text-red-400'}>{ok ? '✓' : '✗'}</span>
                    <span className="font-mono text-gray-300">{svc}</span>
                    {!ok && <span className="text-red-400 truncate" title={msg}>{msg}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Vault read error */}
      {loadError && (
        <div className="mb-4 rounded-lg bg-red-900/30 border border-red-700 p-4 text-red-300 text-sm">
          <p className="font-semibold">Vault read failed</p>
          <p className="text-xs mt-1 font-mono">{loadError}</p>
          <p className="text-xs text-red-400 mt-1">
            Ensure the App Management identity has <strong>Key Vault Secrets User</strong> on <code>{secretStore ?? '?'}</code>.
          </p>
        </div>
      )}

      {loadingAll && (
        <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
          Reading from {secretStore ?? 'vault'}…
        </div>
      )}

      {/* Key table */}
      {selectedTenantId && !loadingAll && rows.length > 0 && (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-700">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide w-44">Service</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide w-20">Algorithm</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Created</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide w-16">Age</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Expires</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Previous</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/60">
              {rows.map(row => (
                <KeyRow key={row.service} row={row} tenantId={selectedTenantId}
                  onOp={runOp} onRefresh={() => refreshOneRow(row.service, selectedTenantId)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!selectedTenantId && (
        <div className="text-center py-16 text-gray-500">Select a tenant to view and manage its service keys.</div>
      )}

      {/* Rotation Health section — cross-tenant read, authenticated as the selected tenant */}
      <div className="mt-6">
        <RotationHealthSection
          tenantId={selectedTenantId}
          status={rotationStatus}
          proxiedVia={rotationProxiedVia}
          loading={rotationLoading}
          error={rotationError}
          open={rotationOpen}
          onToggle={() => setRotationOpen(v => !v)}
        />
      </div>
    </div>
  );
};

// ── Row ───────────────────────────────────────────────────────────────────────

function KeyRow({ row, tenantId, onOp, onRefresh }: {
  row: ServiceRow;
  tenantId: string;
  onOp: (svc: string, op: 'initialize' | 'rotate' | 'cleanup') => void;
  onRefresh: () => void;
}) {
  const { service, status, loadState, op } = row;
  const ck = status?.currentKey;
  const pk = status?.previousKey;
  const busy = loadState === 'loading' || op.state === 'loading';

  return (
    <tr className={`hover:bg-gray-750 transition-colors ${!ck && loadState === 'ok' ? 'opacity-70' : ''}`}>
      {/* Service */}
      <td className="px-4 py-3">
        <span className="font-mono text-xs bg-gray-700 text-blue-300 px-2 py-0.5 rounded">{service}</span>
      </td>

      {/* Algorithm */}
      <td className="px-4 py-3">
        {ck?.algorithm
          ? <span className="font-mono text-xs text-gray-200 bg-gray-700 px-1.5 py-0.5 rounded">{ck.algorithm}</span>
          : <span className="text-gray-600 text-xs">—</span>
        }
      </td>

      {/* Created */}
      <td className="px-4 py-3 text-xs text-gray-400">{fmtDate(ck?.createdDate)}</td>

      {/* Age */}
      <td className="px-4 py-3">
        {ck ? <span className={`text-xs font-mono ${ageColor(ck.ageInDays)}`}>{ck.ageInDays}d</span> : null}
      </td>

      {/* Expires */}
      <td className="px-4 py-3 text-xs text-gray-400">{fmtDate(ck?.expiresOn)}</td>

      {/* Previous key */}
      <td className="px-4 py-3 text-xs text-gray-500">
        {pk
          ? <span title={`Created: ${fmtDate(pk.createdDate)}`}>{pk.ageInDays}d{pk.algorithm ? ` · ${pk.algorithm}` : ''}</span>
          : <span className="text-gray-700">—</span>}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge row={row} />
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <button onClick={onRefresh} disabled={busy} title="Refresh from vault"
            className="p-1.5 rounded text-gray-500 hover:text-gray-200 hover:bg-gray-700 disabled:opacity-40">
            <svg className={`w-3.5 h-3.5 ${loadState === 'loading' ? 'animate-spin' : ''}`}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 2v6h-6M3 12a9 9 0 0115-6.7L21 8M3 22v-6h6M21 12a9 9 0 01-15 6.7L3 16" />
            </svg>
          </button>

          {!ck ? (
            <button onClick={() => onOp(service, 'initialize')} disabled={busy}
              className="px-2.5 py-1 text-xs bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded font-medium"
              title="Initialize via BFF → IDP">
              {op.state === 'loading' ? '…' : 'Init'}
            </button>
          ) : (
            <>
              <button onClick={() => onOp(service, 'rotate')} disabled={busy}
                title={status?.needsRotation ? 'Rotation needed — via BFF → IDP' : 'Rotate via BFF → IDP'}
                className={`px-2.5 py-1 text-xs rounded font-medium disabled:opacity-40 text-white ${status?.needsRotation ? 'bg-orange-600 hover:bg-orange-500' : 'bg-gray-600 hover:bg-gray-500'}`}>
                {op.state === 'loading' ? '…' : 'Rotate'}
              </button>
              <button onClick={() => onOp(service, 'cleanup')} disabled={busy}
                title="Remove previous key version"
                className="px-2.5 py-1 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-gray-300 rounded font-medium">
                Clean
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default KeyManagementPage;

// ── Rotation Health helpers ────────────────────────────────────────────────────

function rotationNextDue(lastRotationAt: string | null): Date | null {
  if (!lastRotationAt) return null;
  const d = new Date(lastRotationAt);
  d.setDate(d.getDate() + 90);
  return d;
}

type HealthLevel = 'Healthy' | 'Degraded' | 'Unhealthy';

function healthLevel(summary: km.RotationHealthSummary): HealthLevel {
  if (summary.rotationAborted || !summary.lastHealthCheckHealthy || summary.minDaysToExpiry < 1) {
    return 'Unhealthy';
  }
  if (summary.minDaysToExpiry < 7) return 'Degraded';
  return 'Healthy';
}

function pairRowStatus(pair: km.RotationPairStatus): 'green' | 'amber' | 'red' {
  if (pair.lastRotationOutcome === 'failed') return 'red';
  const nd = rotationNextDue(pair.lastRotationAt);
  if (!nd) return 'amber';
  const daysUntilDue = (nd.getTime() - Date.now()) / 86400000;
  if (daysUntilDue < 0) return 'red';
  if (daysUntilDue < 7) return 'amber';
  return 'green';
}

// ── RotationHealthSection ─────────────────────────────────────────────────────

function RotationHealthSection({
  tenantId, status, proxiedVia, loading, error, open, onToggle,
}: {
  /** The selected tenant. Empty means nothing has been requested — see the effect that feeds this. */
  tenantId: string;
  status: km.KeyRotationStatusResponse | null;
  /** Which tenant's BFF answered. Never inferred here — the server tells us. */
  proxiedVia: km.RotationStatusProxiedVia | null;
  loading: boolean;
  error: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  const summary = status?.healthSummary;
  const level: HealthLevel | null = summary ? healthLevel(summary) : null;

  const levelBadge = {
    Healthy:   'bg-green-900/40 text-green-400',
    Degraded:  'bg-yellow-900/40 text-yellow-400',
    Unhealthy: 'bg-red-900/50 text-red-400',
  };

  const alertBanner = level && level !== 'Healthy' ? (
    <div className={`mb-4 rounded p-3 text-sm border ${level === 'Unhealthy' ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-yellow-900/30 border-yellow-700 text-yellow-300'}`}>
      <p className="font-semibold">
        {level === 'Unhealthy' ? 'Rotation health is Unhealthy' : 'Rotation health is Degraded'}
      </p>
      <p className="text-xs mt-1">
        {summary?.rotationAborted && 'Last rotation was aborted. '}
        {!summary?.lastHealthCheckHealthy && 'Last health check failed. '}
        {summary !== undefined && summary.minDaysToExpiry < 7 && `Minimum days to key expiry: ${summary.minDaysToExpiry.toFixed(1)}.`}
      </p>
    </div>
  ) : null;

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      {/* Section header / toggle */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-700 hover:bg-gray-750 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-white">Rotation Health</span>
          {level && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${levelBadge[level]}`}>
              {level}
            </span>
          )}
          {/* Which tenant actually answered. Visible while collapsed, which is when it matters most. */}
          {proxiedVia && (
            <span
              className="text-xs text-gray-500 font-mono cursor-help"
              title={`Read through tenant ${proxiedVia.tenantId} at ${proxiedVia.bffUrl}. The IdentityProvider rotation endpoint is cross-tenant, so the rows below are not scoped to this tenant — it is only whose BFF and Keycloak config authenticated the call.`}
            >
              via {proxiedVia.tenantId}
            </span>
          )}
          {loading && (
            <svg className="animate-spin w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? '' : '-rotate-90'}`}
          fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="p-4">
          {!tenantId && (
            <p className="text-gray-500 text-sm py-4">
              Select a tenant to load rotation health. Nothing is requested until you do: the
              endpoint is cross-tenant, but the call has to authenticate as some tenant, and
              letting the server pick one means the panel would report a tenant you never chose.
            </p>
          )}

          {/* Summary bar */}
          {summary && (
            <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-gray-400">
              <span>
                Last health check:&nbsp;
                <span className="text-gray-200">{summary.lastHealthCheckAt ? fmtDate(summary.lastHealthCheckAt) : 'Never'}</span>
              </span>
              <span>
                Min days to expiry:&nbsp;
                <span className={summary.minDaysToExpiry < 7 ? 'text-yellow-400 font-semibold' : 'text-gray-200'}>
                  {summary.minDaysToExpiry.toFixed(1)}
                </span>
              </span>
              {summary.rotationAborted && (
                <span className="text-red-400 font-semibold">Rotation aborted</span>
              )}
            </div>
          )}

          {alertBanner}

          {error && (
            <div className="rounded bg-red-900/30 border border-red-700 p-3 text-red-300 text-sm mb-4">
              <p className="font-semibold">Failed to load rotation status</p>
              <p className="text-xs mt-1 font-mono">{error}</p>
            </div>
          )}

          {loading && !status && (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              Loading rotation status…
            </div>
          )}

          {status && status.pairs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Tenant</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Service</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Last Rotation</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Outcome</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Dry-Run</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Next Due (est.)</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/60">
                  {status.pairs.map(pair => (
                    <RotationPairRow key={`${pair.tenantId}:${pair.serviceName}`} pair={pair} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {status && status.pairs.length === 0 && (
            <p className="text-gray-500 text-sm py-4">No rotation records found.</p>
          )}

          {proxiedVia && (
            <p className="text-xs text-gray-500 mt-4 pt-3 border-t border-gray-700/60">
              Read through tenant <span className="font-mono text-gray-300">{proxiedVia.tenantId}</span>
              {' '}at <span className="font-mono text-gray-300">{proxiedVia.bffUrl}</span>. The
              IdentityProvider rotation endpoint is cross-tenant, so the rows above are not scoped
              to that tenant — it is only whose BFF and Keycloak config authenticated the call.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── RotationPairRow ───────────────────────────────────────────────────────────

function RotationPairRow({ pair }: { pair: km.RotationPairStatus }) {
  const nd = rotationNextDue(pair.lastRotationAt);
  const rowSt = pairRowStatus(pair);

  const outcomeBadge =
    pair.lastRotationOutcome === 'success' ? (
      <span className="text-xs bg-green-900/40 text-green-400 px-1.5 py-0.5 rounded-full">success</span>
    ) : pair.lastRotationOutcome === 'failed' ? (
      <span className="text-xs bg-red-900/50 text-red-400 px-1.5 py-0.5 rounded-full">failed</span>
    ) : (
      <span className="text-gray-600 text-xs">—</span>
    );

  const dryRunCell = pair.lastDryRunAt ? (
    pair.lastDryRunHealthy ? (
      <span className="text-green-400 text-xs" title={`Dry-run: ${fmtDate(pair.lastDryRunAt)}`}>
        <svg className="inline w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path d="M5 13l4 4L19 7" />
        </svg>
      </span>
    ) : (
      <span
        className="text-red-400 text-xs cursor-help"
        title={pair.lastDryRunFailedStep ? `Failed step: ${pair.lastDryRunFailedStep}` : 'Dry-run failed'}
      >
        <svg className="inline w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </span>
    )
  ) : (
    <span className="text-gray-600 text-xs">—</span>
  );

  const statusDot = {
    green: <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5" />,
    amber: <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-1.5" />,
    red:   <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1.5" />,
  }[rowSt];

  const statusLabel = {
    green: <span className="text-green-400">OK</span>,
    amber: <span className="text-yellow-400">Due soon</span>,
    red:   <span className="text-red-400">{pair.lastRotationOutcome === 'failed' ? 'Failed' : 'Overdue'}</span>,
  }[rowSt];

  return (
    <tr className="hover:bg-gray-750 transition-colors">
      <td className="px-3 py-2">
        <span className="font-mono text-xs text-gray-300">{pair.tenantId}</span>
      </td>
      <td className="px-3 py-2">
        <span className="font-mono text-xs bg-gray-700 text-blue-300 px-2 py-0.5 rounded">{pair.serviceName}</span>
      </td>
      <td className="px-3 py-2 text-xs text-gray-400">
        {pair.lastRotationAt ? fmtDate(pair.lastRotationAt) : 'Never'}
      </td>
      <td className="px-3 py-2">{outcomeBadge}</td>
      <td className="px-3 py-2">{dryRunCell}</td>
      <td className="px-3 py-2 text-xs text-gray-400">
        {nd ? fmtDate(nd) : '—'}
      </td>
      <td className="px-3 py-2">
        <span className="inline-flex items-center text-xs">
          {statusDot}{statusLabel}
        </span>
      </td>
    </tr>
  );
}
